'use client';

import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Link from 'next/link';

interface Insight {
  id: string;
  title: string;
  category: string;
  summary: string;
  roi_impact?: { token_savings?: number; accuracy_improvement?: number };
  tags: string[];
  published_at: string;
}

const CATEGORIES = ['all', 'Technique', 'Tool', 'Pattern', 'Research', 'Best Practice'];

function getROIBadge(roi: Insight['roi_impact']): { label: string; variant: string } | null {
  if (!roi) return null;
  if (roi.accuracy_improvement) return { label: `+${roi.accuracy_improvement}% accuracy`, variant: 'success' };
  if (roi.token_savings) return { label: `${roi.token_savings}% savings`, variant: 'default' };
  return null;
}

export default function InsightsPage() {
  const [insights, setInsights] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState<string>('all');
  const [sort, setSort] = useState<'newest' | 'relevant' | 'impact'>('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    const fetchInsights = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(currentPage),
          limit: '12',
          ...(category !== 'all' && { category }),
          sort,
        });

        const response = await fetch(`/api/v1/insights?${params}`);
        const data = await response.json();

        if (data.success) {
          setInsights(data.data);
          setTotalPages(data.pagination.totalPages);
        }
      } catch (error) {
        console.error('Failed to fetch insights:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInsights();
  }, [currentPage, category, sort]);

  const filteredInsights = searchQuery
    ? insights.filter(
        i =>
          i.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          i.summary.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : insights;

  return (
    <div className="min-h-dvh bg-paper">
      <div className="border-b border-rule bg-paper">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="font-serif-display text-3xl text-ink mb-2 text-balance">Prompting Insights</h1>
          <p className="text-lg text-graphite text-pretty">Latest techniques and best practices</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="px-4 py-2 border border-rule bg-paper text-ink"
          />
          <select value={category} onChange={e => { setCategory(e.target.value); setCurrentPage(1); }} className="px-4 py-2 border border-rule bg-paper text-ink">
            {CATEGORIES.map(cat => (
              <option key={cat} value={cat}>{cat === 'all' ? 'All Categories' : cat}</option>
            ))}
          </select>
          <select value={sort} onChange={e => { setSort(e.target.value as "newest" | "impact"); setCurrentPage(1); }} className="px-4 py-2 border border-rule bg-paper text-ink">
            <option value="newest">Newest First</option>
            <option value="impact">Highest Impact</option>
          </select>
        </div>

        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b border-primary"></div>
          </div>
        )}

        {!loading && filteredInsights.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {filteredInsights.map(insight => {
                const roi = getROIBadge(insight.roi_impact);
                return (
                  <Link key={insight.id} href={`/insights/${insight.id}`}>
                    <Card className="h-full hover:shadow-md transition-shadow cursor-pointer rounded-sm border border-rule bg-paper p-6">
                      <div className="flex gap-2 mb-3">
                        <Badge variant="success">{insight.category}</Badge>
                        {roi && <Badge variant={roi.variant as "default" | "success"}>{roi.label}</Badge>}
                      </div>
                      <h3 className="text-lg font-semibold text-ink mb-2">{insight.title}</h3>
                      <p className="text-graphite text-sm mb-4 line-clamp-3">{insight.summary}</p>
                    </Card>
                  </Link>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-rule disabled:opacity-50"
                >
                  Previous
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-4 py-2 tabular-nums ${currentPage === page ? 'bg-primary text-primary-foreground' : 'border border-rule'}`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-rule disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {!loading && filteredInsights.length === 0 && (
          <div className="text-center py-12">
            <p className="text-graphite text-lg">
              {searchQuery ? 'No insights match your search' : 'No insights yet'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
